/**
 * 自分の書式（帳票デザインの2回目、#78、docs/31 §6、DB 045）— 書式の JSON（v: 2）の形。
 *   ・紙の上に「部品」を絶対配置で置く（座標は mm の実数。内部単位を持たない＝010 の決めごと）
 *   ・部品の種類：文字（text）／項目（field＝データを印字。bind で何を出すか）／線（line）／枠（box）／画像（image。3回目）／二次元コード（qr。3回目）
 *   ・明細表（table）は部品とは別に1つ。列ごとに 印字・見出し名・表の左端からの X・幅・文字 pt・寄せ
 *   ・「明細表の下に付けて動かす」＝dock:"table"（表の下端＋3mm から dy）。行数を変えると一緒に下がる
 *   ・消せない部品（法定必須：CLAUDE.md 6.1）は locked。DB のトリガー（fn_check_required_parts）が blocks[] で検査する
 *     → API が els から blocks を作って layout_json に足す（bind → part の対応は PAPER_BIND_PART）
 *   ・画面（エディタ）も API（PDF）も同じ描画 packages/paper を使う。ここは形だけ
 */
import { z } from "zod";
import { PaperKind, PaperOrient } from "./paperBase.js";
import { DEFAULT_LABELS, fillLabels, type Labels } from "./vocabulary.js";

/** 項目（field）が印字するデータ。見積書・請求書で共通（無いものは空で印字） */
export const PaperBind = z.enum([
  "docNo", "docDate", "toName", "toSub", "projectName", "site", "period", "outline", "terms", "valid", "due", "staff",
  "coName", "coRep", "coAddr", "coTel", "coReg", "coPerson",
  "subtotal", "tax", "total", "taxByRate", "notes", "bank", "installment", "pageNo",
  "proviso", "invoiceNo", "docName",
  // {order}（#233 2d）：題（{order}・{orderAck}）・挨拶文・条件の表の6つ・約款／基本契約書の1行・変更協議・請書の欄
  "title", "greet", "receive", "place", "inspect", "payOn", "payMethod", "clause", "negotiation", "ack",
]);
export type PaperBind = z.infer<typeof PaperBind>;
/** {order}だけの項目（ほかの帳票のエディタの候補に出さない） */
export const ORDER_PAPER_BINDS: readonly PaperBind[] = ["title", "greet", "receive", "place", "inspect", "payOn", "payMethod", "clause", "negotiation", "ack"];

/** 項目の名前（エディタの候補と、部品の既定の名前）。{subject} などは会社の言葉（#142 段6 No.34）＝ paperBindLabel で埋める */
export const PAPER_BIND_LABEL: Record<PaperBind, string> = {
  docNo: "番号", docDate: "日付", toName: "宛名", toSub: "宛名の担当者", projectName: "{subject}（件名）", site: "{site}", period: "{period}", outline: "{outline}",
  terms: "支払条件", valid: "有効期限", due: "お支払期限", staff: "担当",
  coName: "自社・会社名", coRep: "自社・代表者名", coAddr: "自社・住所", coTel: "自社・電話", coReg: "自社・登録番号", coPerson: "自社・担当",
  subtotal: "小計", tax: "消費税", total: "合計（税込）", taxByRate: "税率ごとの内訳", notes: "備考", bank: "振込先", installment: "分割の欄", pageNo: "ページ番号",
  proviso: "但し書き（領収書）", invoiceNo: "元の請求書の番号（領収書）", docName: "帳票名と番号（ページの下）",
  title: "題（{order}・{orderAck}）", greet: "挨拶文（{order}）", receive: "受領期日・工期（{order}）", place: "納品場所・工事場所（{order}）", inspect: "検査完了期日（{order}）",
  payOn: "支払期日（{order}）", payMethod: "支払方法（{order}）", clause: "約款・基本契約書の1行（{order}）", negotiation: "変更協議（{order}）", ack: "請書の欄（{order}）",
};
/** 項目の名前を会社の言葉で（#142 段6 No.34）。件名の会社は「件名（件名）」を「件名」に */
export function paperBindLabel(b: PaperBind, labels: Labels = DEFAULT_LABELS): string {
  const v = fillLabels(PAPER_BIND_LABEL[b], labels);
  return b === "projectName" && labels.subject === "件名" ? "件名" : v;
}

/**
 * bind → 010 の template_parts の id（法定必須パーツの検査に使う）。無いものは検査の対象外。
 * 明細表は常に lineItems、題字は text の部品に part:"title" を付ける
 */
export const PAPER_BIND_PART: Partial<Record<PaperBind, string>> = {
  docNo: "documentNo", docDate: "issueDate", toName: "recipientName", projectName: "projectTitle",
  terms: "paymentTerms", valid: "validUntil", due: "paymentDue",
  coName: "senderName", coAddr: "senderAddress", coTel: "senderTel", coReg: "registrationNo", coPerson: "senderAssignee",
  total: "totals", taxByRate: "taxSummary", notes: "notes", bank: "bankAccount",
  // {order}（#233 2d。010 の template_part_rules の order と、20261025_2200 の3つ）
  title: "title", receive: "deliveryDate", place: "deliveryPlace", inspect: "inspectionDate", payOn: "paymentDue", payMethod: "paymentTerms",
  clause: "orderClause", negotiation: "orderNegotiation", ack: "orderAck",
};

/**
 * 消せない部品（doc_type ごと。DB の template_part_rules と同じ。DB が本当の守りで、ここは画面の鍵の印のため）
 *   見積書：題字・番号・日付・宛名・自社名（全帳票共通）
 *   請求書・領収書：＋ 登録番号・明細表・税率ごとの内訳・合計（適格請求書の必須項目。領収書をインボイスにする運用がある）
 */
export const PAPER_REQUIRED_PARTS: Record<PaperKind, readonly string[]> = {
  quote: ["title", "documentNo", "issueDate", "recipientName", "senderName", "lineItems", "totals"],
  invoice: ["title", "documentNo", "issueDate", "recipientName", "senderName", "registrationNo", "lineItems", "taxSummary", "totals"],
  receipt: ["title", "documentNo", "issueDate", "recipientName", "senderName", "registrationNo", "lineItems", "taxSummary", "totals"],
  // {order}（#233 2d）：取適法の明示の事項（受領期日・納品場所・支払期日・支払方法）。010 の template_part_rules と同じ
  order: ["title", "documentNo", "issueDate", "recipientName", "senderName", "lineItems", "totals", "deliveryDate", "deliveryPlace", "paymentDue", "paymentTerms"],
};
/**
 * 建設の会社（field）の{order}の書式で、さらに消せない部品（#233 2d、docs/93 §14.6）：工事名・「記載のない事項は…」の1行
 * （工事場所・工期は 納品場所・受領期日 と同じ部品＝もう消せない）。DB の規則は帳票の種類ごとなので、API が見る（400 PAPER_LOCKED）
 */
export const ORDER_CONSTRUCTION_PARTS: readonly string[] = ["projectTitle", "orderClause"];
/** その会社の書式で消せない部品（{order}は建設の会社なら ORDER_CONSTRUCTION_PARTS も） */
export function requiredPartsFor(kind: PaperKind, o: { construction?: boolean } = {}): readonly string[] {
  return kind === "order" && o.construction ? [...PAPER_REQUIRED_PARTS.order, ...ORDER_CONSTRUCTION_PARTS] : PAPER_REQUIRED_PARTS[kind];
}

export const PaperFont = z.enum(["serif", "sans", "maru"]);
export type PaperFont = z.infer<typeof PaperFont>;
export const PaperAlign = z.enum(["left", "center", "right"]);
export const PaperValign = z.enum(["top", "middle", "bottom"]);
export const PaperRuleStyle = z.enum(["none", "solid", "double", "dotted", "dashed"]);

const mm = z.number().finite();
const color = z.string().regex(/^(#[0-9a-fA-F]{6}|)$/, "色は #RRGGBB");

const ElBase = z.object({
  id: z.string().min(1).max(40),
  /** 部品の名前（部品の一覧に出す。空なら種類の名前） */
  name: z.string().max(60).default(""),
  /** 印字する */
  on: z.boolean().default(true),
  /** 専用帳票モードでは印刷しない「枠」の部品（線・枠・文字）。false なら専用帳票でも印字 */
  frame: z.boolean().default(false),
  /** 消せない（法定必須） */
  locked: z.boolean().default(false),
  /** 010 の template_parts の id（題字は title。項目は bind から自動で決まるので省ける） */
  part: z.string().max(40).optional(),
  /** 組（同じ組は「組で動かす」で一緒に動く） */
  group: z.string().max(40).optional(),
  x: mm,
  y: mm,
  w: mm,
  h: mm,
  /**
   * 明細表の下に付けて動かす（表の下端＋3mm から dy。y は無視）。end＝明細表の下の枠（備考など）のあと（#233 2d：{order}兼請書の切り取り線と請書の欄を
   * 備考の下＝紙のいちばん下に。入らなければ次のページの上から）
   */
  dock: z.enum(["table", "end"]).optional(),
  dy: mm.optional(),
  /**
   * 2ページ目以降にも出す（エディタの「2ページ目以降にも出す」）。プリセットは 番号・日付（2026-10-19）・ページの下の帳票名・ページ番号（#217）。
   * 「番号と表だけ」（secondPage compact）のとき、表より上の部品は上の余白まで寄せて出す（左右は同じ）。省いた書式は前の決まり
   * （番号・日付・ページ番号とその組だけ 2ページ目にも出す）
   */
  repeat: z.boolean().optional(),
  /**
   * 縦に詰める組（#217）。同じ組の部品は上から順に並べ、印刷のとき 値が空の項目・画像の無い画像は詰め、
   * 中身が何行にもなる項目はその分下を押し下げる（発行者欄・頭の欄・宛名。今の紙と同じ見た目にするため）。エディタでは詰めない
   */
  stack: z.string().max(40).optional(),
  /** 重なりを許可（#217）。ほかの部品と重なっても注意に出さない（社印は会社名に重ねて押す慣例＝既定で許可） */
  overlap: z.boolean().optional(),
});

const TextStyle = z.object({
  font: PaperFont.default("sans"),
  size: z.number().min(4).max(72).default(9),
  bold: z.boolean().default(false),
  align: PaperAlign.default("left"),
  valign: PaperValign.default("middle"),
  color: color.default("#1A1A18"),
  /** 行の高さ（文字の大きさの倍。#217。省けば 1.35） */
  lh: z.number().min(1).max(3).optional(),
  /** 字間（文字の大きさの倍。#217：題字「御見積書」は 0.5） */
  ls: z.number().min(0).max(2).optional(),
  /** 太さ（#217。省けば 太字なら 700・ちがえば 400。題字は 600） */
  weight: z.union([z.literal(400), z.literal(500), z.literal(600), z.literal(700)]).optional(),
});

export const PaperTextEl = ElBase.merge(TextStyle).extend({ type: z.literal("text"), text: z.string().max(2000).default("") });
/**
 * 項目（field）。prefix・suffix は値の前後に付ける文字（#216：金額の「¥」「－」・番号の「No. 」。本物の紙と同じ見た目にするため）。
 * 値が空なら付けない。省けば付けない（今までの書式はそのまま＝JSON に足さない）
 */
export const PaperFieldEl = ElBase.merge(TextStyle).extend({
  type: z.literal("field"),
  bind: PaperBind,
  prefix: z.string().max(10).optional(),
  suffix: z.string().max(10).optional(),
  /** 1行に収める（#217）。入りきらなければ文字を小さくする（金額の枠の「¥199,798,500－」が折れない） */
  fit: z.boolean().optional(),
  /** 前に付ける文字の大きさ・書体（#217：「No. 」は 10pt のゴシック、番号は 12pt の明朝。省けば項目と同じ） */
  prefixSize: z.number().min(4).max(72).optional(),
  prefixFont: PaperFont.optional(),
  /** 宛名の敬称（御中・様）の見た目（#217。宛名と宛名の担当者に付ける。省けば同じ大きさで後ろに1字あけて） */
  hon: z.object({ size: z.number().min(4).max(72), gap: z.number().min(0).max(5).default(2) }).optional(),
  /**
   * 項目の見出しを刷る（#233 2d）：値の代わりに、データの見出し（PaperData.heads）を刷る。{order}の条件の表は 工事・仕入で言葉が変わる
   * （工事名⇄件名・工事場所⇄納品場所・工期⇄受領期日・注文金額⇄合計金額）。見出しの部品は消せない部品に数えない
   */
  head: z.boolean().optional(),
});
export const PaperLineEl = ElBase.extend({
  type: z.literal("line"),
  dir: z.enum(["h", "v"]).default("h"),
  style: PaperRuleStyle.default("solid"),
  lw: z.number().min(0.1).max(3).default(0.25),
  color: color.default("#C8C5BC"),
});
export const PaperBoxEl = ElBase.extend({
  type: z.literal("box"),
  style: PaperRuleStyle.default("solid"),
  lw: z.number().min(0.1).max(3).default(0.5),
  color: color.default("#8C8880"),
  fill: color.default(""),
});
/**
 * 画像（046）。asset は 書式の画像（paper_images の id）か 会社のロゴ／社印（company:logo／company:seal。自社情報に登録したもの）。
 *   keep: 縦横比を保つ（枠の中に収める。左上寄せ）／guide: 下敷き（元請の様式のスキャンなど。印刷しない・テスト印刷にも出ない。エディタだけ）
 *   asset の site:map は #133 の現場の地図（#137 で取り外し）。古い書式に残っていても読めるよう受け付けるが、画像は入らない（エディタにも出さない）
 */
export const PaperAssetRef = z.string().regex(/^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|company:(logo|seal)|site:map)$/);
export type PaperAssetRef = z.infer<typeof PaperAssetRef>;
export const PaperImageEl = ElBase.extend({
  type: z.literal("image"),
  asset: PaperAssetRef.nullable().default(null),
  opacity: z.number().min(0).max(1).default(1),
  keep: z.boolean().default(true),
  guide: z.boolean().default(false),
});
/** 二次元コード（046）。bind があれば項目の値（見積番号など）、無ければ text（会社の URL など）。周りは白抜き（4モジュール）。正方形（w＝h） */
export const PaperQrEl = ElBase.extend({
  type: z.literal("qr"),
  text: z.string().max(500).default(""),
  bind: PaperBind.nullable().default(null),
});

export const PaperEl = z.discriminatedUnion("type", [PaperTextEl, PaperFieldEl, PaperLineEl, PaperBoxEl, PaperImageEl, PaperQrEl]);
export type PaperEl = z.infer<typeof PaperEl>;
export type PaperTextEl = z.infer<typeof PaperTextEl>;
export type PaperFieldEl = z.infer<typeof PaperFieldEl>;

export const PaperRule = z.object({ style: PaperRuleStyle.default("solid"), w: z.number().min(0.1).max(3).default(0.25), color: color.default("#C8C5BC") });
export type PaperRule = z.infer<typeof PaperRule>;

/** 明細表の列。id は決まった8つ。x は表の左端からの mm、w は幅 */
export const PaperColId = z.enum(["no", "name", "spec", "qty", "unit", "price", "amount", "notes"]);
export type PaperColId = z.infer<typeof PaperColId>;
/** 列の名前（エディタの欄の読み上げ。#222：名称・規格・備考は会社の言葉＝画面は t() を通す） */
export const PAPER_COL_LABEL: Record<PaperColId, string> = { no: "No.", name: "{lineName}", spec: "{lineSpec}", qty: "数量", unit: "単位", price: "単価", amount: "金額", notes: "{lineNote}" };

export const PaperCol = z.object({
  id: PaperColId,
  on: z.boolean().default(true),
  label: z.string().max(20).default(""),
  x: mm,
  w: mm,
  size: z.number().min(4).max(30).default(9.5),
  align: PaperAlign.default("left"),
  labelAlign: PaperAlign.default("left"),
});
export type PaperCol = z.infer<typeof PaperCol>;

/**
 * 合計を表の中に（#217）：明細のあとに 小計・消費税（税率ごと）・立替金・合計（税込）の行。合計の行は地色・太字・上に太い線。
 * 無ければ前の書式のまま（合計は部品で置く）
 */
export const PaperTableFoot = z.object({
  on: z.boolean().default(true),
  size: z.number().min(4).max(30).default(9),
  totalSize: z.number().min(4).max(30).default(10.5),
  totalFill: color.default("#F0F0F0"),
  /** 小計の上と合計の上の線 */
  rule: PaperRule,
  /** 「（対象 ○円）」の文字 */
  subSize: z.number().min(4).max(30).default(8.5),
  subColor: color.default("#444444"),
});
export type PaperTableFoot = z.infer<typeof PaperTableFoot>;

/** 明細表の下の枠（#217）：備考・訂正の内容・別途・注記。表の幅いっぱい、高さは中身で決まる（中身が無ければ出さない） */
export const PaperTableBoxes = z.object({
  on: z.boolean().default(true),
  /** 表と枠・枠と枠のあいだ（mm） */
  gap: z.number().min(0).max(30).default(4),
  size: z.number().min(4).max(30).default(9),
  /** 枠の高さの下限（mm） */
  minH: z.number().min(0).max(100).default(14),
  rule: PaperRule,
  color: color.default("#1A1A18"),
});
export type PaperTableBoxes = z.infer<typeof PaperTableBoxes>;

/** 内訳ごとの紙（#217。表紙＋内訳の紙）：内訳の見出し（名前と「内訳 1／2　No. …」）と二重線。無ければ内訳は1枚の表に続けて */
export const PaperTableSections = z.object({
  font: PaperFont.default("serif"),
  size: z.number().min(4).max(30).default(14),
  color: color.default("#1A1A18"),
  rule: PaperRule,
  refSize: z.number().min(4).max(30).default(9),
  refColor: color.default("#444444"),
  /** 内訳を続けて刷るときの、前の内訳の下からのあき（mm） */
  gap: z.number().min(0).max(40).default(9),
});
export type PaperTableSections = z.infer<typeof PaperTableSections>;

/** 行の種類ごとの見た目（#217。省けば前の書式の見た目＝地色なし） */
export const PaperTableLook = z.object({
  /** 見出しの行の地色 */
  groupFill: color.default(""),
  /** 内訳の見出しの行（1枚に続けての紙）の地色 */
  secFill: color.default(""),
  /** 小計の行の地色 */
  subFill: color.default(""),
  /** 文章の行の文字の大きさ・色 */
  textSize: z.number().min(4).max(30).optional(),
  textColor: color.default(""),
  /** No. と備考の文字の色 */
  noColor: color.default(""),
  notesColor: color.default(""),
});
export type PaperTableLook = z.infer<typeof PaperTableLook>;

export const PaperTable = z.object({
  x: mm,
  y: mm,
  w: mm,
  /** 1ページの行数と行間（mm）。行数は「ページの下まで空行で埋める」が OFF のときだけ使う */
  rows: z.number().int().min(1).max(80).default(12),
  pitch: z.number().min(3).max(30).default(7),
  size: z.number().min(4).max(30).default(9.5),
  color: color.default("#1A1A18"),
  /** 1行おきの地色（'' で無し） */
  zebra: color.default(""),
  /** 明細が終わったあとの文（最初の空行の名称の欄。'' で無し） */
  restText: z.string().max(20).default("以下余白"),
  header: z.object({
    on: z.boolean().default(true),
    h: z.number().min(3).max(30).default(7),
    size: z.number().min(4).max(30).default(9),
    bold: z.boolean().default(false),
    fill: color.default("#EFEEE9"),
    color: color.default("#1A1A18"),
  }),
  rules: z.object({ outer: PaperRule, head: PaperRule, h: PaperRule, v: PaperRule }),
  cols: z.array(PaperCol).min(1).max(8),
  /** ページの下まで空行で埋める（#217。省けば埋める＝既定 ON。OFF なら1ページ目は rows 行） */
  fill: z.boolean().optional(),
  /** 行の高さを中身に合わせる（#217：名称・規格・単位・備考は折り返し、行が高くなる。今の紙と同じ）。OFF なら1行に切る */
  wrap: z.boolean().optional(),
  /** 欄の中の行の高さ（文字の大きさの倍。省けば 1.3）と左右の余白（mm。省けば 1） */
  lh: z.number().min(1).max(3).optional(),
  padX: z.number().min(0).max(5).optional(),
  foot: PaperTableFoot.optional(),
  boxes: PaperTableBoxes.optional(),
  sections: PaperTableSections.optional(),
  look: PaperTableLook.optional(),
  /**
   * 見出し行・文章（説明）行の枠（#302 C2、2026-11-04）：true なら 見出し行は No〜備考を1つの枠（文字は名称の列から）、
   * 文章の行は No〜名称を1つの枠にして 規格から右の枠は残す（明細の行と同じ枠＝見出しと見分ける）。
   * プリセット（layoutFromPreset）だけが付ける＝前からの自分の書式・出した紙（固めた書式）は今までの形のまま
   */
  spanRows: z.boolean().optional(),
});
export type PaperTable = z.infer<typeof PaperTable>;

export const PaperPageSize = z.enum(["A4", "B5", "A5", "B4", "letter", "custom"]);
export type PaperPageSize = z.infer<typeof PaperPageSize>;
/** 用紙の大きさ（mm、縦置き） */
export const PAPER_SIZES: Record<Exclude<PaperPageSize, "custom">, readonly [number, number]> = { A4: [210, 297], B5: [182, 257], A5: [148, 210], B4: [257, 364], letter: [216, 279] };

export const PaperPage = z.object({
  size: PaperPageSize.default("A4"),
  /** 紙の幅・高さ（mm。向きを反映した値。custom は 50〜600） */
  w: z.number().min(50).max(600),
  h: z.number().min(50).max(600),
  /** 余白の目安（上・右・下・左 mm。印刷の制限ではなく、エディタの点線と警告） */
  margin: z.tuple([mm, mm, mm, mm]).default([15, 15, 15, 15]),
  /** 印字位置の補正（右へ・下へ mm。印刷だけに効く。プリンタのずれ用） */
  offset: z.tuple([z.number().min(-20).max(20), z.number().min(-20).max(20)]).default([0, 0]),
  /** 専用帳票モード：枠の部品（frame）と明細の罫線・見出しを印刷せず、値だけ印字 */
  preprinted: z.boolean().default(false),
  /** 2ページ目以降：compact＝「2ページ目以降にも出す」の部品と明細表だけ（表より上の部品は上の余白に寄せ、表はその下から）／same＝1ページ目と同じ部品 */
  secondPage: z.enum(["compact", "same"]).default("compact"),
});
export type PaperPage = z.infer<typeof PaperPage>;

export const PAPER_LAYOUT_V = 2;

export const PaperLayout = z.object({
  v: z.literal(2),
  kind: PaperKind,
  name: z.string().min(1).max(40),
  orient: PaperOrient,
  page: PaperPage,
  els: z.array(PaperEl).max(200),
  table: PaperTable,
  /** 複製の元（プリセットの key）。見た目の記録だけ */
  basedOn: z.string().max(40).optional(),
  /** {order}兼請書（#233 2d）：請書の欄を{order}の紙にも刷る（署名欄）。省けば{orderAck}の紙だけに刷る */
  combined: z.boolean().optional(),
});
export type PaperLayout = z.infer<typeof PaperLayout>;

/** 自分の書式の一覧の1つ（GET /paper/layouts） */
export const PaperLayoutSummary = z.object({
  id: z.string().uuid(),
  kind: PaperKind,
  slot: z.number().int().min(1).max(3),
  name: z.string(),
  orient: PaperOrient,
  pageSize: PaperPageSize,
  updatedAt: z.string(),
});
export type PaperLayoutSummary = z.infer<typeof PaperLayoutSummary>;

export const PaperLayoutList = z.object({
  /** 枠 1〜3（無い枠は null） */
  quote: z.array(PaperLayoutSummary.nullable()).length(3),
  invoice: z.array(PaperLayoutSummary.nullable()).length(3),
  receipt: z.array(PaperLayoutSummary.nullable()).length(3),
  order: z.array(PaperLayoutSummary.nullable()).length(3),
  /** 自分の書式を保存・適用できるか（オプション。受付開始前は true） */
  available: z.boolean(),
});
export type PaperLayoutList = z.infer<typeof PaperLayoutList>;

/** PUT /paper/layouts/:kind/:slot */
export const PaperLayoutSave = z.object({ layout: PaperLayout });
export type PaperLayoutSave = z.infer<typeof PaperLayoutSave>;

export const PaperLayoutView = z.object({ id: z.string().uuid(), slot: z.number().int(), layout: PaperLayout, updatedAt: z.string() });
export type PaperLayoutView = z.infer<typeof PaperLayoutView>;

/** 紙に流し込むデータ（描画の入口。API が見積・請求書から作る／テスト印刷は見本） */
export const PaperRowKind = z.enum(["detail", "heading", "text", "subtotal", "sheet", "sheetTotal", "foot", "footTotal"]);
export type PaperRowKind = z.infer<typeof PaperRowKind>;
export const PaperRow = z.object({
  kind: PaperRowKind,
  no: z.string().default(""),
  name: z.string().default(""),
  spec: z.string().default(""),
  qty: z.string().default(""),
  unit: z.string().default(""),
  price: z.string().default(""),
  amount: z.string().default(""),
  notes: z.string().default(""),
  /** 表紙の内訳の行（#217）：その内訳の紙の id。規格の後ろに「 (P. n)」（n はその内訳の紙の始まるページ）を付け、押すとそのページへ */
  ref: z.string().optional(),
});
export type PaperRow = z.infer<typeof PaperRow>;

/** 合計の行（#217。PaperTableFoot がある書式で表の中に出す）。sub は「（対象 ○円）」 */
export const PaperFootRow = z.object({ kind: z.enum(["sub", "tax", "reimb", "total"]), label: z.string(), sub: z.string().default(""), amount: z.string() });
export type PaperFootRow = z.infer<typeof PaperFootRow>;

/** 明細表の下の枠の中身（#217。備考・訂正の内容・別途・注記） */
export const PaperBox = z.object({ title: z.string(), text: z.string() });
export type PaperBox = z.infer<typeof PaperBox>;

/** 内訳の紙（#217。PaperTableSections がある書式で、表紙＋内訳の紙のとき） */
export const PaperSection = z.object({
  id: z.string(),
  title: z.string(),
  /** 見出しの右（「内訳 1／2　No. Q-0012-2」） */
  ref: z.string().default(""),
  rows: z.array(PaperRow),
  totalLabel: z.string().default("この内訳の合計"),
  total: z.string(),
  /** 表の下の注記（T2：「※は軽減税率対象」） */
  taxNote: z.string().optional(),
});
export type PaperSection = z.infer<typeof PaperSection>;

export const PaperData = z.object({
  kind: PaperKind,
  fields: z.record(PaperBind, z.string()),
  rows: z.array(PaperRow),
  /** 透かし（下書き・テスト印刷） */
  watermark: z.string().default(""),
  /**
   * 明細表の下の注記（T2：軽減の行があるとき「※は軽減税率対象」）。明細表の部品の一部として描く＝書式で消せない・部品は増やさない。
   * 無ければ何も描かない（今の紙と同じ）
   */
  taxNote: z.string().optional(),
  /** 合計の行（#217。表の中に合計を出す書式だけが使う） */
  foot: z.array(PaperFootRow).optional(),
  /** 明細表の下の枠（#217） */
  boxes: z.array(PaperBox).optional(),
  /** 内訳の紙（#217）。あれば rows は表紙の行 */
  sections: z.array(PaperSection).optional(),
  /** 内訳ごとに改ページ（会社の設定。#217） */
  sectionBreak: z.boolean().optional(),
  /**
   * 明細表の上の欄（#217：分割請求の「ご契約金額／これまでのご請求／今回のご請求／残り」・#267：締め請求書の「ご請求期間／対象」）。
   * strong は太字にする欄の番号、align は値の寄せ（既定 right＝金額）
   */
  before: z.object({ heads: z.array(z.string()), vals: z.array(z.string()), strong: z.number().int().optional(), align: z.enum(["right", "center"]).optional() }).optional(),
  /**
   * 明細表の上の「お支払い予定」の表（#267 3b：分割払いの請求書だけ。before の下）。回・お支払期限・金額の行と合計。
   * 無ければ何も描かない（今の紙と同じ）
   */
  schedule: z
    .object({
      title: z.string(),
      rows: z.array(z.object({ label: z.string(), date: z.string(), amount: z.string() })),
      total: z.object({ label: z.string(), amount: z.string() }),
    })
    .optional(),
  /** 値が空のときに代わりに刷る文（#217：下書きの番号の欄に「（下書き・番号は出すときに付きます）」）。前後の文字は付けない */
  alt: z.record(z.string(), z.string()).optional(),
  /**
   * 宛名の敬称（#217）。あるときは toName・toSub に敬称を含めず、描き手が最後の行の後ろに付ける（宛名の担当者があればそちら）。
   * 無ければ前の形（toName・toSub に敬称が入っている）
   */
  hon: z.string().optional(),
  /** 項目の見出し（#233 2d：見出しを刷る項目＝PaperFieldEl.head が読む。{order}の条件の表は 工事・仕入で言葉が変わる） */
  heads: z.partialRecord(PaperBind, z.string()).optional(),
  /**
   * 請書の欄（#233 2d。項目 ack が描く）。無ければ何も描かない＝{order}の紙（兼請書でない書式）。
   * head＝兼請書の請書の頭（題・宛名＝発注者・番号・工事名・金額）。kind＝電子（承諾の記録）／紙（署名欄＝空の欄と「印」）
   */
  ack: z
    .object({
      head: z.object({ title: z.string(), to: z.string(), rows: z.array(z.tuple([z.string(), z.string()])) }).optional(),
      greet: z.string(),
      title: z.string(),
      kind: z.enum(["screen", "paper"]),
      rows: z.array(z.tuple([z.string(), z.string()])),
    })
    .optional(),
});
export type PaperData = z.infer<typeof PaperData>;
export type PaperAck = NonNullable<PaperData["ack"]>;

/** POST /paper/test-pdf・GET /paper/data の入口。docId があれば本物の見積・請求書・領収書の中身で（046） */
export const PaperTestPrint = z.object({ layout: PaperLayout, docId: z.string().uuid().optional() });
export type PaperTestPrint = z.infer<typeof PaperTestPrint>;

/** テスト印刷に使える本物の帳票（GET /paper/docs?kind=。新しい順に 10 件） */
export const PaperDocRef = z.object({ id: z.string().uuid(), no: z.string(), title: z.string(), date: z.string(), status: z.string(), client: z.string() });
export type PaperDocRef = z.infer<typeof PaperDocRef>;
export const PaperDocList = z.object({ items: z.array(PaperDocRef) });
export type PaperDocList = z.infer<typeof PaperDocList>;

/** 書式に置く画像（046。GET /paper/images） */
export const PaperImage = z.object({
  id: z.string().uuid(),
  name: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  bytes: z.number().int(),
  createdAt: z.string(),
  /** 書式（消したものも）か出した紙が指している＝消せない */
  inUse: z.boolean(),
});
export type PaperImage = z.infer<typeof PaperImage>;
export const PaperImageList = z.object({
  items: z.array(PaperImage),
  /** 自社情報に登録してあるロゴ・社印（あれば company:logo／company:seal で置ける） */
  company: z.object({ logo: z.boolean(), seal: z.boolean() }),
});
export type PaperImageList = z.infer<typeof PaperImageList>;
export const PAPER_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const PAPER_IMAGE_MAX_PX = 2400;
export const PAPER_IMAGE_MSG = {
  type: "画像は PNG か JPEG にしてください",
  tooBig: "画像は 2MB までです",
  tooLarge: "画像の大きさは 2400px までです（画面で縮めます）",
  broken: "画像を読めませんでした",
  inUse: "この画像は書式（削除した書式や、発行済みの帳票を含む）が使っているので削除できません",
  notFound: "その画像はありません",
  nameRequired: "画像の名前を入れてください",
  uploaded: "画像を登録しました",
  removed: "画像を消しました",
  limit: "画像は 30 枚までです",
} as const;
export const PAPER_IMAGE_LIMIT = 30;

export const PAPER_LAYOUT_MSG = {
  locked: "法令上、消せない部品があります",
  slotInvalid: "書式の枠は 1〜3 です",
  notFound: "その書式はありません",
  optionOff: "カスタム書式はオプション「帳票デザイン」で使えます",
  saved: "書式を保存しました",
  removed: "書式を削除しました（発行済みの帳票は変わりません）",
  nameRequired: "書式の名前を入れてください",
  pcOnly: "書式の編集はパソコン（幅 1100px 以上）で行ってください",
  docNotFound: "その帳票はありません",
} as const;
